import React from 'react';
import { Heart, Zap, Brain, Smile, Moon, Shield, Dumbbell } from 'lucide-react';
import { Goal } from '@/context/UserDataContext';
import { FormSection } from '../../shared/FormSection';
import { Badge } from '@/components/ui/badge';

interface GoalOption {
  value: Goal;
  label: string;
  icon: React.ReactNode;
}

interface GoalsSelectionProps {
  selectedGoals: { value: Goal; priority: number }[];
  onToggle: (goal: Goal) => void;
  error?: string;
}

const goalOptions: GoalOption[] = [
  { value: 'general_wellbeing', label: 'General Wellbeing', icon: <Heart className="h-5 w-5" /> },
  { value: 'energy', label: 'Energy', icon: <Zap className="h-5 w-5" /> },
  { value: 'mental_clarity', label: 'Mental Clarity', icon: <Brain className="h-5 w-5" /> },
  { value: 'mood', label: 'Mood', icon: <Smile className="h-5 w-5" /> },
  { value: 'sleep', label: 'Sleep', icon: <Moon className="h-5 w-5" /> },
  { value: 'immune_support', label: 'Immune Support', icon: <Shield className="h-5 w-5" /> },
  { value: 'fitness', label: 'Fitness', icon: <Dumbbell className="h-5 w-5" /> }
];

const GoalsSelection = ({ selectedGoals, onToggle, error }: GoalsSelectionProps) => {
  const isSelected = (goal: Goal) => selectedGoals.some(g => g.value === goal);
  const getPriority = (goal: Goal) => selectedGoals.find(g => g.value === goal)?.priority;
  const canSelect = selectedGoals.length < 3;
  const canUnselect = (goal: Goal) => {
    const priority = getPriority(goal);
    return priority === selectedGoals.length; // Can only unselect the last selected goal
  };

  return (
    <FormSection
      question="What are your current health goals?"
      helperText="Select up to 3 areas you're hoping to improve, in order of priority."
      error={error}
    >
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        {goalOptions.map((goal) => {
          const selected = isSelected(goal.value);
          const priority = getPriority(goal.value);
          const disabled = !selected && !canSelect;
          
          return (
            <button
              key={goal.value}
              type="button"
              onClick={() => onToggle(goal.value)}
              disabled={disabled || (selected && !canUnselect(goal.value))}
              className={`
                relative flex flex-col items-center justify-center p-4 rounded-2xl border transition-all duration-300
                ${selected 
                  ? 'border-primary bg-primary/5 text-primary' 
                  : disabled
                    ? 'border-border/30 bg-muted/30 text-muted-foreground/40 cursor-not-allowed'
                    : 'border-border/100 hover:border-primary/30 hover:bg-primary/5 text-muted-foreground/90'}
              `}
            >
              {selected && priority && (
                <Badge 
                  variant="secondary" 
                  className="absolute top-2 right-2 h-6 w-6 p-0 flex items-center justify-center bg-primary text-white/80"
                >
                  {priority}
                </Badge>
              )}
              <div className={`mb-2`}>
                {goal.icon}
              </div>
              <span className="text-sm font-medium text-center">{goal.label}</span>
            </button>
          );
        })}
      </div>
    </FormSection>
  );
};

export default GoalsSelection;
